import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  House,
  ChartPieSlice,
  Compass,
  ClockCounterClockwise,
  Bell,
  CaretDown,
  User,
  Gear,
  ArrowSquareOut,
  Question,
  SignOut,
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useAuth, api } from '../../context/AuthContext';
import { cn } from '../../lib/utils';

const LOGO = 'https://customer-assets.emergentagent.com/job_invest-portal-59/artifacts/c24cz0ev_Color%20logo%20-%20no%20background.png';
const LAST_LOCATION_KEY = 'flippex:app:last-location';

export const APP_TABS = [
  { label: 'Home', path: '/app/home', icon: House },
  { label: 'Portfolio', path: '/app/portfolio', icon: ChartPieSlice },
  { label: 'Explore', path: '/app/explore', icon: Compass },
  { label: 'Activity', path: '/app/activity', icon: ClockCounterClockwise },
];

export const rememberAppLocation = (path) => {
  try {
    sessionStorage.setItem(LAST_LOCATION_KEY, path);
  } catch (e) {
    /* storage unavailable */
  }
};

export const getAppReturnPath = () => {
  try {
    return sessionStorage.getItem(LAST_LOCATION_KEY) || '/app/home';
  } catch (e) {
    return '/app/home';
  }
};

const AppHeader = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  useEffect(() => {
    if (!user) return;
    api.get('/portfolio/v2/activity?limit=5')
      .then((res) => setNotifications(res.data.events || []))
      .catch(() => setNotifications([]));
  }, [user]);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <header className="sticky top-0 z-50 w-full bg-slate-900 border-b border-white/[0.07]" data-testid="app-header">
      <div className="mx-auto w-full max-w-[1280px] px-5 md:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/app/home" className="flex-shrink-0" data-testid="app-logo-link">
            <img src={LOGO} alt="Flippex" className="h-7 w-auto" />
          </Link>

          <nav className="hidden md:flex items-center gap-1" data-testid="app-desktop-nav">
            {APP_TABS.map((tab) => (
              <Link
                key={tab.label}
                to={tab.path}
                className={cn(
                  'px-3.5 py-2 text-sm rounded-lg transition-colors',
                  isActive(tab.path)
                    ? 'text-white font-medium bg-white/10'
                    : 'text-white/70 hover:text-white hover:bg-white/[0.07]'
                )}
                data-testid={`app-nav-${tab.label.toLowerCase()}`}
              >
                {tab.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-1.5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
                  data-testid="app-notifications-button"
                >
                  <Bell size={20} className="text-white/75" />
                  {notifications.length > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-primary text-[10px] font-medium text-white flex items-center justify-center">
                      {notifications.length}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 bg-white border-black/10 shadow-xl">
                <div className="px-4 py-3 border-b border-black/5">
                  <p className="text-sm font-semibold">Notifications</p>
                </div>
                <div className="max-h-[300px] overflow-y-auto" data-testid="app-notifications-list">
                  {notifications.length === 0 ? (
                    <p className="px-4 py-6 text-center text-sm text-foreground-muted">Nothing new right now.</p>
                  ) : (
                    notifications.map((n, i) => (
                      <button
                        key={i}
                        onClick={() => navigate(n.security_id ? `/app/portfolio/${n.security_id}` : '/app/activity')}
                        className="w-full text-left px-4 py-3 hover:bg-black/5 border-b border-black/5 last:border-0"
                        data-testid="app-notification-item"
                      >
                        <p className="text-sm font-medium">{n.title}</p>
                        {n.subtitle && <p className="text-xs text-foreground-muted mt-0.5">{n.subtitle}</p>}
                      </button>
                    ))
                  )}
                </div>
                <DropdownMenuSeparator className="bg-black/5" />
                <DropdownMenuItem onClick={() => navigate('/app/activity')} data-testid="app-notifications-view-all">
                  View all activity
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-1.5 p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                  data-testid="app-profile-menu-trigger"
                >
                  <span className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center text-sm font-medium text-primary">
                    {user?.first_name?.[0]}{user?.last_name?.[0]}
                  </span>
                  <CaretDown size={14} className="text-white/70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60 bg-white border-black/10 shadow-xl">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
                  <p className="text-xs text-foreground-muted truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator className="bg-black/5" />
                <p className="px-3 pt-1.5 pb-1 text-[10px] uppercase tracking-[0.16em] text-foreground-muted">Account</p>
                <DropdownMenuItem onClick={() => navigate('/app/settings?tab=profile')} data-testid="app-menu-profile">
                  <User className="mr-2" size={17} /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/app/settings')} data-testid="app-menu-settings">
                  <Gear className="mr-2" size={17} /> Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-black/5" />
                <p className="px-3 pt-1.5 pb-1 text-[10px] uppercase tracking-[0.16em] text-foreground-muted">Flippex</p>
                <DropdownMenuItem onClick={() => navigate('/')} data-testid="app-menu-visit-website">
                  <ArrowSquareOut className="mr-2" size={17} /> Visit Flippex website
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-black/5" />
                <DropdownMenuItem onClick={() => navigate('/faqs')} data-testid="app-menu-help">
                  <Question className="mr-2" size={17} /> Help &amp; support
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="app-menu-signout">
                  <SignOut className="mr-2" size={17} /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
};

const AppTabBar = () => {
  const location = useLocation();
  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-black/[0.06] pb-[env(safe-area-inset-bottom)]"
      data-testid="app-tab-bar"
    >
      <div className="flex items-stretch">
        {APP_TABS.map((tab) => {
          const Icon = tab.icon;
          const active = location.pathname === tab.path || location.pathname.startsWith(`${tab.path}/`);
          return (
            <NavLink
              key={tab.label}
              to={tab.path}
              className={cn(
                'flex-1 flex flex-col items-center gap-1 py-2.5 text-[10px] transition-colors',
                active ? 'text-primary' : 'text-foreground-muted'
              )}
              data-testid={`app-tab-${tab.label.toLowerCase()}`}
            >
              <Icon size={20} weight={active ? 'fill' : 'regular'} />
              {tab.label}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};

/** Persistent authenticated investor app shell: compact header, app navigation, no marketing footer. */
export const AppShell = ({ children, hideTabBar = false }) => {
  const location = useLocation();

  useEffect(() => {
    rememberAppLocation(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="app-shell">
      <AppHeader />
      <main
        className={cn(
          'flex-1',
          hideTabBar ? '' : 'pb-[calc(4rem+env(safe-area-inset-bottom))] md:pb-0'
        )}
      >
        {children}
      </main>
      {!hideTabBar && <AppTabBar />}
    </div>
  );
};

export default AppShell;
